import type { FunctionalAreasContextProps } from "$root/App/context";
import { ReviewEntity } from "$root/pages/ReviewInstruments.const";
import { ViewModeEnum } from "@eu.mdotm.api/smart-workspaces";

/**
 * One piece of "what the user is currently viewing" context, in both forms we need:
 * a short `label` for the visible badge (see {@link describeAppContext}) and the full
 * `line` injected into the hidden system message (see {@link buildAppContextSystemMessage}).
 */
type ContextDescriptor = { label: string; line: string };

/**
 * Build a descriptor for a portfolio-studio entity (portfolio, universe, …). The badge
 * `label` prefers the entity's name (falling back to just the type while it loads); the
 * system-message `line` always carries the UUID so the assistant can ground tool calls,
 * naming the entity too when the name is known. Returns `null` when there's no entity.
 */
function entityDescriptor(
	type: string,
	noun: string,
	entity: { uuid?: string; name?: string } | undefined,
): ContextDescriptor | null {
	if (!entity?.uuid) return null;
	return {
		label: entity.name ? `${type}: ${entity.name}` : type,
		line: entity.name
			? `- Viewing the ${noun} "${entity.name}" (UUID "${entity.uuid}").`
			: `- Viewing the ${noun} with UUID "${entity.uuid}".`,
	};
}

/**
 * Build a descriptor for the open smart workspace. Unlike a plain entity descriptor this also
 * names the open Table/Chart tab, so a request like "add a filter to the chart" resolves against
 * what the user is actually looking at instead of the workspace's last-saved view — the two can
 * disagree once a tab is reachable by URL (see `open_page`'s `tab` param) independently of it.
 */
function workspaceDescriptor(workspace: FunctionalAreasContextProps["workspace"]): ContextDescriptor | null {
	if (!workspace?.uuid) return null;
	const tab = workspace.view === ViewModeEnum.Chart ? "Chart" : "Table";
	const named = workspace.name ? `"${workspace.name}" ` : "";
	return {
		label: workspace.name ? `Workspace: ${workspace.name}` : "Workspace",
		line: `- Viewing the smart workspace ${named}(UUID "${workspace.uuid}"), currently on the ${tab} tab.`,
	};
}

/**
 * Map a review-page entity kind onto (a) the business `noun` shown to the user and (b) the
 * `entityType` value the review write tools expect. The tool schema's enum is
 * investment/reference/benchmark/universe (a `reference` is the backend TARGET_INVESTMENT),
 * so we spell that value out in the context to let the assistant ground its tool calls.
 */
const REVIEW_ENTITY_INFO: Record<ReviewEntity, { noun: string; entityType: string }> = {
	[ReviewEntity.INVESTMENT]: { noun: "portfolio", entityType: "investment" },
	[ReviewEntity.TARGET_INVESTMENT]: { noun: "reference", entityType: "reference" },
	[ReviewEntity.BENCHMARK]: { noun: "benchmark", entityType: "benchmark" },
	[ReviewEntity.UNIVERSE]: { noun: "universe", entityType: "universe" },
};

/**
 * Build a descriptor for the composition **review** step. Unlike a plain entity descriptor
 * this always carries the `entityType` and UUID the review write tools are keyed on, and
 * frames the screen so the assistant knows it can resolve the flagged holdings' proxies —
 * but only apply them once the user explicitly confirms (the tool enforces the same). Returns
 * `null` when there's no entity under review.
 */
function reviewDescriptor(review: FunctionalAreasContextProps["review"]): ContextDescriptor | null {
	if (!review?.uuid || !review.entity) return null;
	const info = REVIEW_ENTITY_INFO[review.entity];
	if (!info) return null;
	const named = review.name ? `"${review.name}" ` : "";
	return {
		label: review.name ? `Review: ${review.name}` : "Composition Review",
		line:
			`- On the composition review step of the ${info.noun} ${named}(entity type "${info.entityType}", ` +
			`UUID "${review.uuid}"). Some holdings still need a proxy / risk-model resolution before the ` +
			`composition can be saved; you can help resolve them, and apply the changes only after the user ` +
			`explicitly confirms.`,
	};
}

/** How a lab column's source reads to a human, per its `VirtualInvestmentSourceType`. */
const LAB_SOURCE_NOUN: Record<string, string> = {
	PORTFOLIO: "a portfolio",
	INVESTMENT_PROPOSAL: "a portfolio proposal",
	BENCHMARK: "a benchmark",
	REFERENCE: "a reference",
	VIRTUAL: "another lab column",
};

/**
 * Build a descriptor for the Portfolio Lab page.
 *
 * The lab is the one screen whose context is a LIST: it holds several columns side by side, each a
 * what-if copy of a different entity, so "this column" / "the second one" cannot be bound without
 * naming them all. Every column is listed with its own uuid — which is what the lab tools are keyed
 * on, and is NOT the uuid of the entity it was copied from; addressing the latter would edit the
 * saved portfolio instead of the copy.
 *
 * The framing matters as much as the ids: a lab edit is cheap and reversible, and it reaches the
 * saved entity only through an explicit commit. But describing the setup passively — the lab is a
 * scratch copy, separate from the real entity — was OBSERVED not to be enough: the assistant still
 * manufactured a lab-vs-real disambiguation dialog for an ordinary "increase this holding to 5%"
 * request (MDOT-25221), because nothing told it the lab is the default. So the populated-columns
 * line now carries an EXPLICIT instruction — default every action to the lab, do not ask — rather
 * than trusting the passive framing to prevent it. This generalises the delete-only scoping fix
 * from MDOT-25216 to every action, not just removal.
 *
 * The editor's group-by is named too, with what one ROW on screen is in it: without it the assistant
 * cannot know that "the GOV ITA bucket" is a category, or that "the largest positions" are categories
 * in one view and holdings in another, and asks the user for what is already on screen (MDOT-25922).
 *
 * Returns `null` when the area is not registered (`columns` absent — the page is not open). An OPEN
 * but empty lab is a real context, distinct from that, and says so.
 */
function portfolioLabDescriptor(lab: FunctionalAreasContextProps["portfolioLab"]): ContextDescriptor | null {
	const columns = lab?.columns;
	if (!columns) return null;

	const scenario = lab?.scenarioId
		? ` The whole lab is currently bound to the historical scenario "${lab.scenarioId}", so its metrics and NAV series describe that period.`
		: "";
	if (columns.length === 0) {
		return {
			label: "Portfolio Lab",
			line:
				"- On the Portfolio Lab page — a what-if workspace whose columns are scratch copies of portfolios, " +
				"proposals, benchmarks or references — with no column loaded yet, so there is nothing in it to read or " +
				`change until one is added.${scenario}`,
		};
	}
	const listed = columns
		.map((column) => {
			const source = column.sourceType ? LAB_SOURCE_NOUN[column.sourceType] : undefined;
			return `  - "${column.name ?? "(unnamed)"}" — column UUID "${column.columnUuid}"${
				source ? `, a copy of ${source}` : ""
			}`;
		})
		.join("\n");
	return {
		label: `Portfolio Lab: ${columns.length} column${columns.length === 1 ? "" : "s"}`,
		line:
			"- On the Portfolio Lab page — a what-if workspace where each column is a scratch copy the user reshapes " +
			`without touching the saved entity. Its ${columns.length} column${columns.length === 1 ? " is" : "s are"}:\n${listed}\n` +
			"Address a column by its COLUMN uuid, never by the uuid of the entity it was copied from. Changes to a " +
			"column stay inside the lab: they reach the saved entity only through an explicit commit, which needs the " +
			`user's confirmation.${scenario} ` +
			'DEFAULT EVERY ACTION TO THE LAB, WITHOUT ASKING: a request like "increase Swisscom AG\'s weight to 5%" ' +
			"means the loaded column, not the real portfolio — do not offer a lab-vs-real choice or any other " +
			"disambiguation for it. Touch the real, saved entity only when the user's own words say so explicitly " +
			'("on the live portfolio", "update the real one", "directly on the saved composition"), or through the ' +
			"lab's own explicit commit (portfolio_lab-apply_to_investment) — never as a guess and never by asking." +
			groupByLine(lab?.groupBy),
	};
}

/**
 * The editor's grouping, and what one row on screen is in it — as the editor's view modes build
 * them (`deriveCompositionViewMode`, `buildCompositionRows`). Empty before the page reports one.
 */
function groupByLine(groupBy: NonNullable<FunctionalAreasContextProps["portfolioLab"]>["groupBy"]): string {
	if (!groupBy) return "";
	const { firstLevel, classificationUuid, classificationName } = groupBy;
	const classification = classificationUuid
		? `${classificationName ? `"${classificationName}" ` : ""}(classificationUuid "${classificationUuid}")`
		: undefined;
	const named =
		" When the user names a classification instead, pass its name as `classificationUuid` — the lab tools resolve it.";
	if (firstLevel === "ALLOCATION" && classification) {
		return (
			`\nThe composition editor is grouped by Allocation › ${classification}: each row on screen is a CATEGORY of ` +
			"that classification, with its instruments (a nested portfolio's under it) inside. In this view the user's " +
			'"positions" ARE these categories: "the largest positions", "top holdings" or "the biggest rows" is answered ' +
			"with the categories ranked by weight, never with instruments. Every read, so every portfolio_lab-get_column " +
			"call, passes that uuid (its `categories` give each category's weight and number of instruments); a group " +
			'named ("sell the whole <category>") is a `kind: "category"` edit with that uuid, and `normalise` takes it ' +
			`too. Do not ask which grouping or classification is meant.${named}`
		);
	}
	if (firstLevel === "ALLOCATION") {
		return (
			"\nThe composition editor is ungrouped: each row on screen is ONE INSTRUMENT, those inside nested portfolios " +
			"listed among the direct ones, so the largest positions are the largest instruments across both. Read them " +
			`with portfolio_lab-get_column without a classification.${named}`
		);
	}
	return (
		"\nThe composition editor is grouped by Holdings: its top-level rows are one per nested portfolio and ONE " +
		'"Single instrument" row carrying the summed weight of every direct instrument, each row with its ' +
		'instruments listed under it. So "the largest positions" are those top-level rows ranked by weight — nested ' +
		'portfolios and "Single instrument" — read with portfolio_lab-get_column' +
		(classification
			? `, and the instruments under each row are sub-grouped by ${classification} — pass that uuid to see the split.`
			: ".") +
		named
	);
}

/**
 * Collect the current-screen context as an ordered list of descriptors. The single
 * source of truth behind both the hidden system message and the user-facing badge, so
 * the two can never drift (what the badge advertises is exactly what gets sent).
 */
function collectAppContext(state: FunctionalAreasContextProps): ContextDescriptor[] {
	const items: ContextDescriptor[] = [];

	const entities = [
		state.portfolio?.proposal
			? entityDescriptor("Proposal", "pending proposal of the portfolio", state.portfolio)
			: entityDescriptor("Portfolio", "portfolio", state.portfolio),
		entityDescriptor("Universe", "universe", state.universe),
		entityDescriptor("Market View", "market view", state.marketView),
		entityDescriptor("Reference", "reference", state.reference),
		entityDescriptor("Benchmark", "benchmark", state.benchmark),
		entityDescriptor("Storyfolio", "storyfolio (commentary template)", state.storyfolio),
		entityDescriptor("Report", "report template", state.report),
		workspaceDescriptor(state.workspace),
	];
	for (const entity of entities) {
		if (entity) items.push(entity);
	}

	const review = reviewDescriptor(state.review);
	if (review) items.push(review);

	const portfolioLab = portfolioLabDescriptor(state.portfolioLab);
	if (portfolioLab) items.push(portfolioLab);

	// The market screens' badge labels carry their selection, for the same reason an entity's carries
	// its name: the region / filter codes ARE what identifies "here" on these pages (they have no
	// UUID), so a bare "Market Outlook Focus" advertises the page while hiding the one thing the
	// hidden message actually grounds the answer on — and the user can't tell the level of detail
	// Sphere can see, which is what the badge exists to show.
	if (state.hmm?.region) {
		items.push({
			label: `Market Regime: ${state.hmm.region}`,
			line: `- Viewing the market regime analysis for region "${state.hmm.region}".`,
		});
	}

	// The dashboard is the market screen with NO selection, and saying so is the point: the assistant
	// otherwise reads an unfiltered market question against one market's default filters (the outlook
	// tools each fall back to a landing market), answering about EQ/MARKETS while the user is looking
	// at all three asset classes side by side. Naming the widgets is what makes "this" resolvable —
	// the page's content is its identity, the way a filter triple is the outlook pages'.
	if (state.marketDashboard?.open) {
		items.push({
			label: "Market Dashboard",
			line:
				"- On the Market Dashboard — MDOTM's cross-asset overview, showing the asset-class overview " +
				"indicator, the outlook table for equity, fixed income and commodities side by side, and the " +
				'dashboard market commentary. Nothing is filtered here, so a question about "this" or "the market" ' +
				"means the whole cross-asset picture: read it with the dashboard tool rather than picking an asset " +
				"class and area.",
		});
	}

	const outlook = describeFilters(state.outlook);
	if (outlook) {
		items.push({
			label: `Market Outlook: ${outlook.codes}`,
			line: `- On the Market Outlook page, filtered by ${outlook.sentence}.`,
		});
	}
	const outlookFocus = describeFilters(state.outlookFocus);
	if (outlookFocus) {
		items.push({
			label: `Market Outlook Focus: ${outlookFocus.codes}`,
			line: `- On the Market Outlook Focus page, filtered by ${outlookFocus.sentence}.`,
		});
	}

	return items;
}

/**
 * Serializes the global {@link FunctionalAreasContextProps} state into a hidden
 * system message describing what the user is currently viewing in the platform.
 *
 * This is injected into every chat turn so the assistant can ground its answers
 * in the user's current screen (selected portfolio, regime region, outlook
 * filters, …) without the user having to restate it. Returns `null` when there
 * is no meaningful context to share, in which case no system message is added.
 */
function buildAppContextSystemMessage(state: FunctionalAreasContextProps): string | null {
	const items = collectAppContext(state);
	if (items.length === 0) {
		return null;
	}

	return [
		"The user is currently viewing the following in the MDOTM platform UI.",
		"Use it as background context to make your answers more relevant. Do not mention or repeat this context unless the user asks about it.",
		// Naming the entity is not enough on its own: a request like "update the chart in this
		// workspace" carries no UUID, and without this the assistant re-discovers a target from a
		// listing tool and can act on a same-shaped entity the user never opened. Binding the
		// deictic reference here is the only place that knows which entity the user actually means.
		'When the user refers to an entity implicitly ("this portfolio", "the workspace", "here"), it is the one listed below — use its UUID directly. Never resolve such a reference by searching or listing entities, and if the request names an entity that is NOT listed below, ask which one they mean rather than picking one.',
		// The market-insights screens have no UUID: what identifies "here" is a set of
		// filter codes, and they are quoted below in exactly the vocabulary the
		// market_insights-* / market_regime-* tools take (asset class "EQ"/"FI"/"CO", the
		// area and sector as the page's selector spells them, the region as "EU"/"US"/
		// "JP"/"GLOBAL"). Saying so is what stops "how is this positioned?" from becoming
		// either a clarifying question or a lookup of the tools' default market.
		'When the user asks about the market data on screen ("this asset class", "here", "this region"), pass the filter values and region quoted below to the market-data tools verbatim — they are already in the vocabulary those tools expect. Do not substitute a default and do not ask the user to restate them.',
		...items.map((item) => item.line),
	].join("\n");
}

/**
 * Describe the current-screen context for the user-facing badge: a short `label`
 * (e.g. `"Portfolio · Benchmark"`) plus a `signature` — the full hidden-message body,
 * which changes whenever the underlying context changes. The signature lets the UI key
 * a dismissal to a specific context, so hiding the badge on one screen doesn't suppress
 * it after the user navigates elsewhere. Returns `null` when there's nothing to share.
 */
function describeAppContext(state: FunctionalAreasContextProps): { label: string; signature: string } | null {
	const items = collectAppContext(state);
	if (items.length === 0) {
		return null;
	}
	return {
		label: items.map((item) => item.label).join(" · "),
		// Identity-only signature (UUIDs, region, filters — no names). Names load
		// asynchronously; keying on them would re-show a badge the user hid while the
		// name was still loading. This changes only when the user navigates elsewhere.
		signature: JSON.stringify([
			state.portfolio?.uuid,
			state.portfolio?.proposal,
			state.universe?.uuid,
			state.marketView?.uuid,
			state.reference?.uuid,
			state.benchmark?.uuid,
			state.storyfolio?.uuid,
			state.report?.uuid,
			state.workspace?.uuid,
			state.workspace?.view,
			state.hmm?.region,
			state.outlook,
			state.outlookFocus,
			state.marketDashboard?.open,
			state.review?.uuid,
			state.review?.entity,
			// The lab's identity is WHICH columns are open, so adding or removing one is a genuinely
			// new context and re-shows a dismissed badge; renaming one is not. `undefined` (page not
			// open) and `[]` (open, empty) stay distinct, as they are in the message.
			state.portfolioLab?.columns?.map((column) => column.columnUuid),
			state.portfolioLab?.scenarioId,
		]),
	};
}

/**
 * Describe a market page's filter selection in the two forms this module needs.
 *
 * `sentence` names each level in the vocabulary the market-data tools take ("asset class", "area",
 * "sector"), so the assistant can pass the quoted values straight through. `codes` is the bare
 * selection for the badge, joined with "/" because " · " is already the badge's own separator
 * between context items — reusing it would read as three separate contexts.
 *
 * Returns `null` when nothing is selected: the pages register their area before their option tree
 * resolves, and a page whose filters are all empty is not yet a context worth sharing.
 */
function describeFilters(filters?: {
	firstFilter?: string;
	secondFilter?: string;
	thirdFilter?: string;
}): { codes: string; sentence: string } | null {
	const codes: string[] = [];
	const parts: string[] = [];
	if (filters?.firstFilter) {
		codes.push(filters.firstFilter);
		parts.push(`asset class "${filters.firstFilter}"`);
	}
	if (filters?.secondFilter) {
		codes.push(filters.secondFilter);
		parts.push(`area "${filters.secondFilter}"`);
	}
	if (filters?.thirdFilter) {
		codes.push(filters.thirdFilter);
		parts.push(`sector "${filters.thirdFilter}"`);
	}
	return parts.length > 0 ? { codes: codes.join("/"), sentence: parts.join(", ") } : null;
}

export { buildAppContextSystemMessage, describeAppContext };
